import React, { useEffect, useMemo, useRef } from 'react';
import { FileClock, FileWarning, History } from 'lucide-react';
import { usePatientExternalEmrImport } from '../../../hooks/usePatientExternalEmrImport';
import {
  historyFailed,
  historyPending,
  normalizePatientImportStatus,
  rendersPending,
  rendersUnrecovered,
  type ExternalEmrImportStatus,
  type ExternalEmrPatientState,
} from '../../../services/externalEmrPatients';
import {
  HISTORY_FAILED_LINE,
  HISTORY_FAILED_REFRESH_HINT,
  HISTORY_PENDING_LINE,
  RENDERS_PENDING_LINE,
  RENDERS_UNRECOVERED_LINE,
} from './externalEmrPatientCopy';

interface ExternalEmrRendersLineProps {
  patientId: string;
  external: ExternalEmrPatientState;
  /** When the read carrying `external.import` began (ms); null trusts it. */
  seedAt?: number | null;
  className?: string;
  /**
   * Called once when the earlier episodes stop arriving (landed or failed),
   * so an open chart can re-read its episodes instead of hiding the warning
   * over a stale episode list.
   */
  onHistorySettled?: () => void;
  /**
   * This viewer sees Refresh from External EMR on the chart. Only then does a
   * failed history line name it as the next step.
   */
  canRefresh?: boolean;
}

const LINE = 'inline-flex items-center gap-1 text-red-700';
const ICON = 'h-3.5 w-3.5 shrink-0';

function historyLine(
  status: ExternalEmrImportStatus,
  canRefresh: boolean
): React.ReactNode {
  if (historyPending(status)) {
    return (
      <p className={LINE} data-testid='history-pending'>
        <History className={ICON} aria-hidden='true' />
        {HISTORY_PENDING_LINE}
      </p>
    );
  }
  if (historyFailed(status)) {
    return (
      <p className={LINE} data-testid='history-failed'>
        <FileWarning className={ICON} aria-hidden='true' />
        {canRefresh
          ? `${HISTORY_FAILED_LINE} ${HISTORY_FAILED_REFRESH_HINT}`
          : HISTORY_FAILED_LINE}
      </p>
    );
  }
  return null;
}

function documentsLine(status: ExternalEmrImportStatus): React.ReactNode {
  if (rendersPending(status)) {
    return (
      <p className={LINE} data-testid='renders-pending'>
        <FileClock className={ICON} aria-hidden='true' />
        {RENDERS_PENDING_LINE}
      </p>
    );
  }
  if (rendersUnrecovered(status)) {
    return (
      <p className={LINE} data-testid='renders-unrecovered'>
        <FileWarning className={ICON} aria-hidden='true' />
        {RENDERS_UNRECOVERED_LINE}
      </p>
    );
  }
  return null;
}

/**
 * What the imported chart is still missing, after the chart itself has
 * landed: its earlier episodes, when the import brought the recent ones
 * first, and its documents. Both arrive as their own queue rows, so this
 * re-reads once a minute while either is queued or running, and stops when
 * they settle. It never turns the Imported pill back into Importing.
 */
export const ExternalEmrRendersLine: React.FC<ExternalEmrRendersLineProps> = ({
  patientId,
  external,
  seedAt = null,
  className = '',
  onHistorySettled,
  canRefresh = false,
}) => {
  const seed = useMemo(
    () => normalizePatientImportStatus(external.import),
    [external.import]
  );
  const { status } = usePatientExternalEmrImport(patientId, {
    seed,
    seedAt,
    enabled: external.state === 'imported',
  });
  const arriving = historyPending(status);
  const wasArriving = useRef(arriving);
  useEffect(() => {
    if (wasArriving.current && !arriving) onHistorySettled?.();
    wasArriving.current = arriving;
  }, [arriving, onHistorySettled]);
  if (external.state !== 'imported') return null;

  // Every line is red so a chart still missing episodes or documents is not
  // mistaken for a complete one. The icon and wording tell arriving from not
  // recoverable. Missing episodes come first: they matter more.
  const history = historyLine(status, canRefresh);
  const documents = documentsLine(status);
  if (!history && !documents) return null;
  return (
    <div className={`flex flex-col ${className}`}>
      {history}
      {documents}
    </div>
  );
};
